import { Head, router, useForm, usePage } from '@inertiajs/react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Group,
  NativeSelect,
  Paper,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
  UnstyledButton,
} from '@mantine/core'
import { CheckCircle2, CircleDot, Pencil } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import MarkdownBody from '@/components/MarkdownBody'
import QuestionMessage from '@/components/QuestionMessage'
import { ActorBadge, IssueStateBadge, IssueTypeBadge } from '@/components/badges'
import { timeAgo } from '@/lib/session-utils'
import type {
  IssueAgentOption,
  IssueCommentProps,
  IssueDetailProps,
  IssueShowPageProps,
  QuestionItem,
} from '@/types'

function StateIcon({ state }: { state: IssueDetailProps['state'] }) {
  if (state === 'closed') {
    return <CheckCircle2 size={16} color="var(--mantine-color-violet-6)" />
  }
  return <CircleDot size={16} color="var(--mantine-color-teal-6)" />
}


function CommentCard({ comment }: { comment: IssueCommentProps }) {
  return (
    <Paper withBorder p={0} radius="md">
      <Group
        px="md"
        py="xs"
        gap="xs"
        style={{
          backgroundColor: 'var(--mantine-color-gray-0)',
          borderBottom: '1px solid var(--mantine-color-default-border)',
          borderRadius: 'var(--mantine-radius-md) var(--mantine-radius-md) 0 0',
        }}
      >
        <Avatar
          size="sm"
          radius="xl"
          color={comment.author_type === 'agent' ? 'violet' : 'teal'}
        >
          {(comment.author_name || '?').charAt(0).toUpperCase()}
        </Avatar>
        <Text fw={600} size="sm">
          {comment.author_name || 'Unknown'}
        </Text>
        <ActorBadge type={comment.author_type} />
        <Text c="dimmed" size="sm">
          commented {timeAgo(comment.created_at)}
        </Text>
      </Group>
      <Box px="md" py="sm">
        <MarkdownBody>{comment.body}</MarkdownBody>
      </Box>
    </Paper>
  )
}

function DescriptionCard({ issue }: { issue: IssueDetailProps }) {
  return (
    <Paper withBorder p={0} radius="md">
      <Group
        px="md"
        py="xs"
        gap="xs"
        style={{
          backgroundColor: 'var(--mantine-color-gray-0)',
          borderBottom: '1px solid var(--mantine-color-default-border)',
          borderRadius: 'var(--mantine-radius-md) var(--mantine-radius-md) 0 0',
        }}
      >
        <Avatar
          size="sm"
          radius="xl"
          color={issue.author_type === 'agent' ? 'violet' : 'teal'}
        >
          {(issue.author_name || '?').charAt(0).toUpperCase()}
        </Avatar>
        <Text fw={600} size="sm">
          {issue.author_name || 'Unknown'}
        </Text>
        <ActorBadge type={issue.author_type} />
        <Text c="dimmed" size="sm">
          opened {timeAgo(issue.created_at)}
        </Text>
      </Group>
      <Box px="md" py="sm">
        {issue.description ? (
          <MarkdownBody>{issue.description}</MarkdownBody>
        ) : (
          <Text c="dimmed" fs="italic" size="sm">
            No description provided.
          </Text>
        )}
      </Box>
    </Paper>
  )
}

function AddCommentForm({ projectId, issueId }: { projectId: string; issueId: string }) {
  const { csrf_token } = usePage().props

  const { data, setData, post, processing, reset } = useForm({
    issue_comment: { body: '' },
    authenticity_token: csrf_token as string,
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post(`/projects/${projectId}/issues/${issueId}/comments`, {
      preserveScroll: true,
      onSuccess: () => reset(),
    })
  }

  return (
    <Paper component="form" onSubmit={handleSubmit} withBorder p={0} radius="md">
      <Box px="md" py="sm">
        <Textarea
          placeholder="Leave a comment... (Markdown supported)"
          autosize
          minRows={4}
          variant="unstyled"
          value={data.issue_comment.body}
          onChange={(e) => setData('issue_comment', { body: e.target.value })}
        />
      </Box>
      <Group
        px="md"
        py="xs"
        justify="flex-end"
        style={{
          borderTop: '1px solid var(--mantine-color-default-border)',
        }}
      >
        <Button
          type="submit"
          size="sm"
          disabled={!data.issue_comment.body.trim()}
          loading={processing}
        >
          Comment
        </Button>
      </Group>
    </Paper>
  )
}

function resolveCurrentAssignee(issue: IssueDetailProps): string {
  if (issue.assignee_id === 'user') return 'user'
  if (issue.assignee_id) return issue.assignee_id
  return 'none'
}

function EditIssueForm({
  projectId,
  issue,
  agents,
  onCancel,
}: {
  projectId: string
  issue: IssueDetailProps
  agents: IssueAgentOption[]
  onCancel: () => void
}) {
  const { csrf_token } = usePage().props

  const { data, setData, patch, processing } = useForm({
    issue: {
      title: issue.title,
      description: issue.description || '',
      state: issue.state,
      issue_type: issue.issue_type,
    },
    assignee: resolveCurrentAssignee(issue),
    authenticity_token: csrf_token as string,
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    patch(`/projects/${projectId}/issues/${issue.id}`, {
      preserveScroll: true,
      onSuccess: () => onCancel(),
    })
  }

  return (
    <Paper component="form" onSubmit={handleSubmit} p="lg" withBorder>
      <Stack gap="md">
        <TextInput
          label="Title"
          value={data.issue.title}
          onChange={(e) => setData('issue', { ...data.issue, title: e.target.value })}
          required
        />

        <Textarea
          label="Description (Markdown supported)"
          autosize
          minRows={6}
          value={data.issue.description}
          onChange={(e) => setData('issue', { ...data.issue, description: e.target.value })}
        />

        <Group grow>
          <NativeSelect
            label="State"
            value={data.issue.state}
            onChange={(e) =>
              setData('issue', {
                ...data.issue,
                state: e.target.value as IssueDetailProps['state'],
              })
            }
            data={[
              { value: 'open', label: 'Open' },
              { value: 'in_progress', label: 'In Progress' },
              { value: 'feedback_required', label: 'Feedback Required' },
              { value: 'feedback_provided', label: 'Feedback Provided' },
              { value: 'closed', label: 'Closed' },
            ]}
          />

          <NativeSelect
            label="Type"
            value={data.issue.issue_type}
            onChange={(e) =>
              setData('issue', {
                ...data.issue,
                issue_type: e.target.value as IssueDetailProps['issue_type'],
              })
            }
            data={[
              { value: 'task', label: 'Task' },
              { value: 'fix', label: 'Fix' },
              { value: 'enhancement', label: 'Enhancement' },
              { value: 'chore', label: 'Chore' },
              { value: 'question', label: 'Question' },
              { value: 'generic', label: 'Generic' },
            ]}
          />
        </Group>

        <NativeSelect
          label="Assignee"
          value={data.assignee}
          onChange={(e) => setData('assignee', e.target.value)}
          data={[
            { value: 'none', label: 'No one' },
            { value: 'user', label: 'Me' },
            ...agents.map((a) => ({ value: a.id, label: a.name })),
          ]}
        />

        <Group>
          <Button type="submit" loading={processing}>
            Save changes
          </Button>
          <Button type="button" variant="default" onClick={onCancel}>
            Cancel
          </Button>
        </Group>
      </Stack>
    </Paper>
  )
}

function Sidebar({
  issue,
  projectId,
  onEdit,
}: {
  issue: IssueDetailProps
  projectId: string
  onEdit: () => void
}) {
  const { csrf_token } = usePage().props
  const isClosed = issue.state === 'closed'

  const toggleState = () => {
    const newState = isClosed ? 'open' : 'closed'
    router.patch(
      `/projects/${projectId}/issues/${issue.id}`,
      {
        issue: { state: newState },
        authenticity_token: csrf_token as string,
      },
      { preserveScroll: true },
    )
  }

  return (
    <Stack gap="lg">
      {/* Assignee */}
      <Box>
        <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="xs">
          Assignee
        </Text>
        {issue.assignee_name ? (
          <Group gap="xs">
            <Avatar
              size="sm"
              radius="xl"
              color={issue.assignee_type === 'agent' ? 'violet' : 'teal'}
            >
              {issue.assignee_name.charAt(0).toUpperCase()}
            </Avatar>
            <Text size="sm">{issue.assignee_name}</Text>
            {issue.assignee_type && <ActorBadge type={issue.assignee_type} />}
          </Group>
        ) : (
          <Text size="sm" c="dimmed">
            No one assigned
          </Text>
        )}
      </Box>

      <Divider />

      {/* Type */}
      <Box>
        <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="xs">
          Type
        </Text>
        <IssueTypeBadge issueType={issue.issue_type} />
        {issue.issue_type === 'generic' && (
          <Text size="sm" c="dimmed">
            Generic
          </Text>
        )}
      </Box>

      <Divider />

      {/* Timestamps */}
      <Box>
        <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb="xs">
          Created
        </Text>
        <Text size="sm">{timeAgo(issue.created_at)}</Text>
        {issue.closed_at && (
          <>
            <Text size="xs" fw={600} c="dimmed" tt="uppercase" mt="sm" mb="xs">
              Closed
            </Text>
            <Text size="sm">{timeAgo(issue.closed_at)}</Text>
          </>
        )}
      </Box>

      <Divider />

      {/* Actions */}
      <Stack gap="xs">
        <Button variant="default" size="sm" leftSection={<Pencil size={14} />} onClick={onEdit}>
          Edit issue
        </Button>
        <Button
          variant={isClosed ? 'light' : 'default'}
          color={isClosed ? 'teal' : 'violet'}
          size="sm"
          leftSection={isClosed ? <CircleDot size={14} /> : <CheckCircle2 size={14} />}
          onClick={toggleState}
        >
          {isClosed ? 'Reopen issue' : 'Close issue'}
        </Button>
      </Stack>
    </Stack>
  )
}

export default function IssueShow() {
  const { project, issue, comments, agents, ancestors, csrf_token } = usePage<IssueShowPageProps>().props
  const [editing, setEditing] = useState(false)

  const isOpen = issue.state !== 'closed'

  return (
    <>
      <Head title={`${issue.title} - ${project.name}`} />

      <Container size="lg" py="md">
        <Stack gap="md">
          {/* Title header */}
          <Box>
            <Group gap="sm" align="baseline" wrap="nowrap">
              <Title order={2} style={{ flex: 1 }}>
                {issue.title}
              </Title>
            </Group>

            <Group gap="xs" mt="xs">
              <IssueStateBadge state={issue.state} />
              <Text size="sm" c="dimmed">
                {issue.author_name || 'Unknown'} opened this issue {timeAgo(issue.created_at)}
              </Text>
            </Group>
          </Box>

          <Divider />

          {editing ? (
            <EditIssueForm
              projectId={project.id}
              issue={issue}
              agents={agents}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <Grid gutter="xl">
              {/* Main content column */}
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Stack gap="md">
                  {/* Question workflow alerts */}
                  {issue.issue_type === 'question' &&
                    issue.state === 'feedback_required' &&
                    issue.metadata?.questions &&
                    issue.question_tool_call_id && (
                      <QuestionMessage
                        toolCallId={issue.question_tool_call_id}
                        questions={issue.metadata.questions as QuestionItem[]}
                        onResume={async (body) => {
                          await fetch(
                            `/projects/${project.id}/issues/${issue.id}/comments/answer`,
                            {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'X-CSRF-Token': (csrf_token as string) || '',
                              },
                              body: JSON.stringify(body),
                            },
                          )
                          router.reload()
                        }}
                      />
                    )}

                  {issue.issue_type === 'question' && issue.state === 'feedback_provided' && (
                    <Alert color="indigo" title="Feedback submitted">
                      Waiting for the agent to process your answers.
                    </Alert>
                  )}

                  {issue.issue_type === 'question' && issue.state === 'closed' && (
                    <Alert color="gray" title="Question answered">
                      This question has been resolved.
                    </Alert>
                  )}

                  {/* Description */}
                  <DescriptionCard issue={issue} />

                  {/* Comments */}
                  {comments.map((comment) => (
                    <CommentCard key={comment.id} comment={comment} />
                  ))}

                  {/* Add comment form */}
                  <AddCommentForm projectId={project.id} issueId={issue.id} />
                </Stack>
              </Grid.Col>

              {/* Sidebar column */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Sidebar
                  issue={issue}
                  projectId={project.id}
                  onEdit={() => setEditing(true)}
                />
              </Grid.Col>
            </Grid>
          )}
        </Stack>
      </Container>
    </>
  )
}
